import { useEffect, useRef, useState } from 'react';
import { 
  Settings, Globe, Shield, Phone, Mail, MapPin, 
  Share2, Camera, Info,
  CheckCircle2, AlertCircle, Save, Trash2, Building, Layout, RefreshCcw,
  Clock, XCircle, Plus
} from 'lucide-react';
import '../../styles/admin.css';
import { fetchSettings, saveSettings } from '../../lib/settings';
import { getFileUrl } from '../../lib/pb';
import AddressAutocomplete from '../../components/admin/AddressAutocomplete';

const DAYS = [
  { key: 'monday',    label: 'Lundi' },
  { key: 'tuesday',   label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday',  label: 'Jeudi' },
  { key: 'friday',    label: 'Vendredi' },
  { key: 'saturday',  label: 'Samedi' },
  { key: 'sunday',    label: 'Dimanche' },
];

const DEFAULT_HOURS = {
  monday:    { open: '08:00', close: '17:00', active: true },
  tuesday:   { open: '08:00', close: '17:00', active: true },
  wednesday: { open: '08:00', close: '17:00', active: true },
  thursday:  { open: '08:00', close: '17:00', active: true },
  friday:    { open: '08:00', close: '17:00', active: true },
  saturday:  { open: '08:00', close: '12:00', active: false },
  sunday:    { open: '08:00', close: '12:00', active: false },
};

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState('identity');
  const [record, setRecord] = useState(null);
  const [siteName, setSiteName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [legalInfo, setLegalInfo] = useState('');
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialInstagram, setSocialInstagram] = useState('');
  const [socialLinkedin, setSocialLinkedin] = useState('');
  const [logo, setLogo] = useState(null);
  const [mapEmbed, setMapEmbed] = useState('');
  const [businessHours, setBusinessHours] = useState(DEFAULT_HOURS);
  const [closures, setClosures] = useState([]);
  const [newClosure, setNewClosure] = useState({ date: '', label: '' });
  const [baseAddress, setBaseAddress] = useState('');
  const [baseLat, setBaseLat] = useState('');
  const [baseLon, setBaseLon] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await fetchSettings();
        if (res) {
          setRecord(res);
          setSiteName(res.site_name || '');
          setPhone(res.phone || '');
          setEmail(res.email || '');
          setAddress(res.address || '');
          setLegalInfo(res.legal_info || '');
          setMapEmbed(res.map_embed || '');
          const socialsObj = res.socials || {};
          setSocialFacebook(socialsObj.facebook || '');
          setSocialInstagram(socialsObj.instagram || '');
          setSocialLinkedin(socialsObj.linkedin || '');
          if (res.logo) setLogo({ type: 'existing', name: res.logo });
          if (res.business_hours && typeof res.business_hours === 'object') setBusinessHours({ ...DEFAULT_HOURS, ...res.business_hours });
          if (Array.isArray(res.exceptional_closures)) setClosures(res.exceptional_closures);
          setBaseAddress(res.base_address || '');
          setBaseLat(res.base_lat || '');
          setBaseLon(res.base_lon || '');
        }
      } catch (e) {
        setError(e.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const compressImage = (file, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
          canvas.width = width; canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (!blob) return reject(new Error('Échec compression'));
            resolve(blob);
          }, 'image/jpeg', quality);
        } catch(err) { reject(err); }
      };
      img.onerror = () => reject(new Error("Image invalide"));
      img.src = URL.createObjectURL(file);
    });
  };

  async function handleLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const finalFile = new File([compressed], file.name, { type: 'image/jpeg' });
      setLogo({ type: 'new', file: finalFile });
    } catch(err) { setError("Erreur compression logo"); }
  }

  const logoUrl = logo
    ? (logo.type === 'existing' && record ? getFileUrl(record, logo.name) : URL.createObjectURL(logo.file))
    : '';

  async function handleSubmit(e) {
    if(e) e.preventDefault();
    setSaving(true); setError(''); setSuccess('');

    const data = {
      site_name: siteName, phone, email, address,
      legal_info: legalInfo, map_embed: mapEmbed,
      socials: { 
        facebook: socialFacebook || undefined, 
        instagram: socialInstagram || undefined, 
        linkedin: socialLinkedin || undefined 
      },
      business_hours: businessHours,
      exceptional_closures: closures,
      base_address: baseAddress,
      base_lat: baseLat ? parseFloat(baseLat) : null,
      base_lon: baseLon ? parseFloat(baseLon) : null,
    };

    if (logo) {
      if (logo.type === 'new') data.logo = logo.file;
    } else {
      data.logo = null;
    }

    try {
      const saved = await saveSettings(record, data);
      setRecord(saved);
      if (saved.logo) setLogo({ type: 'existing', name: saved.logo });
      setSuccess('Configuration enregistrée avec succès ✨');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-page">
      <header className="settings-header">
        <div className="header-text">
          <h1>Centre de Configuration</h1>
          <p className="muted">Gérez l'identité, le contact et l'infrastructure de votre plateforme.</p>
        </div>
        <button className={`btn-save-settings ${saving ? 'loading' : ''}`} onClick={handleSubmit} disabled={saving}>
          {saving ? <RefreshCcw size={18} className="spin"/> : <Save size={18}/>}
          <span>Enregistrer les modifications</span>
        </button>
      </header>

      {success && <div className="alert-success-s"><CheckCircle2 size={18}/> {success}</div>}
      {error && <div className="alert-error-s"><AlertCircle size={18}/> {error}</div>}

      <div className="settings-container">
        <aside className="settings-nav">
           <button className={activeTab === 'identity' ? 'active' : ''} onClick={() => setActiveTab('identity')}>
              <Layout size={20}/> <span>Identité Visuelle</span>
           </button>
           <button className={activeTab === 'contact' ? 'active' : ''} onClick={() => setActiveTab('contact')}>
              <Phone size={20}/> <span>Contact & Accès</span>
           </button>
           <button className={activeTab === 'social' ? 'active' : ''} onClick={() => setActiveTab('social')}>
              <Globe size={20}/> <span>Réseaux Sociaux</span>
           </button>
           <button className={activeTab === 'legal' ? 'active' : ''} onClick={() => setActiveTab('legal')}>
              <Shield size={20}/> <span>Expert & Légal</span>
           </button>
           <button className={activeTab === 'hours' ? 'active' : ''} onClick={() => setActiveTab('hours')}>
              <Clock size={20}/> <span>Horaires</span>
           </button>
           <button className={activeTab === 'closures' ? 'active' : ''} onClick={() => setActiveTab('closures')}>
              <XCircle size={20}/> <span>Fermetures</span>
           </button>
        </aside>

        <main className="settings-content">
           <form onSubmit={handleSubmit}>
              {activeTab === 'identity' && (
                <div className="settings-card animation-slide-up">
                   <h3><div className="h-icon"><Layout size={18}/></div> Identité du Site</h3>
                   <div className="field-group">
                      <div className="s-field">
                         <label>Nom de la plateforme</label>
                         <input value={siteName} onChange={e => setSiteName(e.target.value)} placeholder="ex: AEF 32" />
                      </div>
                      <div className="logo-upload">
                         <label>Logo Officiel</label>
                         <div className="logo-box">
                            <div className="logo-preview">
                               {logoUrl ? <img src={logoUrl} alt="Logo" /> : <div className="no-logo"><Building size={32}/></div>}
                            </div>
                            <div className="logo-actions">
                               <button type="button" className="btn-upload" onClick={() => fileInputRef.current?.click()}>
                                  <Camera size={16}/> Changer le logo
                               </button>
                               <button type="button" className="btn-del-mini" onClick={() => setLogo(null)}><Trash2 size={14}/></button>
                               <p className="hint">PNG/JPG de haute qualité recommandé.</p>
                            </div>
                            <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={handleLogoChange}/>
                         </div>
                      </div>
                   </div>
                </div>
              )}

              {activeTab === 'contact' && (
                <div className="settings-card animation-slide-up">
                   <h3><div className="h-icon"><Phone size={18}/></div> Coordonnées de l'entreprise</h3>
                   <div className="field-grid">
                      <div className="s-field">
                         <label><Phone size={14}/> Téléphone</label>
                         <input value={phone} onChange={e => setPhone(e.target.value)} />
                      </div>
                      <div className="s-field">
                         <label><Mail size={14}/> Email de contact</label>
                         <input type="email" value={email} onChange={e => setEmail(e.target.value)} />
                      </div>
                   </div>
                   <div className="s-field">
                      <label><MapPin size={14}/> Adresse Postale</label>
                      <textarea rows={3} value={address} onChange={e => setAddress(e.target.value)} />
                   </div>
                   <div className="s-field">
                      <label><Globe size={14}/> Google Maps (Embed URL)</label>
                      <textarea rows={2} value={mapEmbed} onChange={e => setMapEmbed(e.target.value)} />
                      <div className="info-box-s"><Info size={14}/> Collez l'URL source de l'iframe Google Maps pour afficher la carte sur votre site.</div>
                   </div>

                   <div style={{ marginTop: 20, paddingTop: 20, borderTop: '2px dashed #f1f5f9' }}>
                     <h4 style={{ margin: '0 0 15px 0', fontSize: 13, fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                       <Building size={16} style={{ color: '#0ea5e9' }}/> Adresse de base (Dépôt / Logistique)
                     </h4>
                     <p style={{ fontSize: 11, color: '#64748b', marginBottom: 15, fontWeight: 600 }}>Cette adresse est utilisée pour calculer les temps de trajet vers les chantiers dans le planning.</p>
                     
                     <div className="s-field">
                        <label>Point de départ par défaut</label>
                        <AddressAutocomplete 
                           value={baseAddress} 
                           onChange={setBaseAddress} 
                           onSelect={s => { setBaseAddress(s.label); setBaseLat(s.lat); setBaseLon(s.lon); }}
                           placeholder="ex: 2 chemin des religieuses 32220 Lombez"
                        />
                     </div>
                     <div className="field-grid">
                        <div className="s-field">
                           <label>Latitude</label>
                           <input value={baseLat} onChange={e => setBaseLat(e.target.value)} placeholder="ex: 43.4751" disabled />
                        </div>
                        <div className="s-field">
                           <label>Longitude</label>
                           <input value={baseLon} onChange={e => setBaseLon(e.target.value)} placeholder="ex: 0.9105" disabled />
                        </div>
                     </div>
                   </div>
                </div>
              )}

              {activeTab === 'social' && (
                <div className="settings-card animation-slide-up">
                   <h3><div className="h-icon"><Globe size={18}/></div> Présence Sociale</h3>
                   <div className="social-fields">
                      <div className="s-field social">
                         <div className="icon-fb"><Globe size={20}/></div>
                         <div className="s-entry">
                            <label>Lien Facebook</label>
                            <input value={socialFacebook} onChange={e => setSocialFacebook(e.target.value)} placeholder="https://..." />
                         </div>
                      </div>
                      <div className="s-field social">
                         <div className="icon-ig"><Camera size={20}/></div>
                         <div className="s-entry">
                            <label>Lien Instagram</label>
                            <input value={socialInstagram} onChange={e => setSocialInstagram(e.target.value)} placeholder="https://..." />
                         </div>
                      </div>
                      <div className="s-field social">
                         <div className="icon-in"><Share2 size={20}/></div>
                         <div className="s-entry">
                            <label>Lien LinkedIn</label>
                            <input value={socialLinkedin} onChange={e => setSocialLinkedin(e.target.value)} placeholder="https://..." />
                         </div>
                      </div>
                   </div>
                </div>
              )}

              {activeTab === 'legal' && (
                <div className="settings-card animation-slide-up">
                   <h3><div className="h-icon"><Shield size={18}/></div> Informations Expert</h3>
                   <div className="s-field">
                      <label>Numéro de SIRET</label>
                      <input value={legalInfo} onChange={e => setLegalInfo(e.target.value)} placeholder="ex: 123 456 789 00012" />
                   </div>
                   <div className="info-box-s danger">
                      <AlertCircle size={16}/>
                      <div>
                         <strong>Paramètres sensibles</strong>
                         <p>Ces données sont utilisées pour les mentions légales et le bas de page.</p>
                      </div>
                   </div>
                </div>
              )}

              {activeTab === 'hours' && (
                 <div className="settings-card animation-slide-up">
                   <h3><div className="h-icon"><Clock size={18}/></div> Horaires d'ouverture</h3>
                   <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                     {DAYS.map(({ key, label }) => (
                       <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px', background: businessHours[key]?.active ? '#f0fdf4' : '#f8fafc', borderRadius: 16, border: `1.5px solid ${businessHours[key]?.active ? '#bbf7d0' : '#e2e8f0'}`, transition: 'all 0.2s' }}>
                         <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', minWidth: 110 }}>
                           <input type="checkbox" checked={!!businessHours[key]?.active}
                             onChange={e => setBusinessHours(h => ({ ...h, [key]: { ...h[key], active: e.target.checked } }))}
                             style={{ width: 18, height: 18, accentColor: '#22c55e', cursor: 'pointer' }}
                           />
                           <span style={{ fontWeight: 800, fontSize: 14, color: businessHours[key]?.active ? '#15803d' : '#94a3b8' }}>{label}</span>
                         </label>
                         {businessHours[key]?.active ? (
                           <>
                             <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                               <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Ouverture</span>
                               <input type="time" value={businessHours[key]?.open || '08:00'}
                                 onChange={e => setBusinessHours(h => ({ ...h, [key]: { ...h[key], open: e.target.value } }))}
                                 style={{ padding: '8px 12px', borderRadius: 10, border: '1.5px solid #d1fae5', background: '#fff', fontWeight: 800, fontSize: 14 }}
                               />
                             </div>
                             <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                               <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Fermeture</span>
                               <input type="time" value={businessHours[key]?.close || '17:00'}
                                 onChange={e => setBusinessHours(h => ({ ...h, [key]: { ...h[key], close: e.target.value } }))}
                                 style={{ padding: '8px 12px', borderRadius: 10, border: '1.5px solid #d1fae5', background: '#fff', fontWeight: 800, fontSize: 14 }}
                               />
                             </div>
                           </>
                         ) : (
                           <span style={{ fontSize: 13, color: '#cbd5e1', fontWeight: 700, fontStyle: 'italic' }}>Fermé</span>
                         )}
                       </div>
                     ))}
                   </div>
                   <div style={{ marginTop: 20, padding: '14px 18px', background: '#f0f9ff', borderRadius: 14, border: '1px solid #bae6fd', fontSize: 12, fontWeight: 700, color: '#0369a1', display: 'flex', gap: 10, alignItems: 'center' }}>
                     <Info size={16}/> Ces horaires servent au calcul automatique des heures de mission dans le planning.
                   </div>
                 </div>
               )}

                              {activeTab === 'closures' && (
                  <div className="settings-card animation-slide-up">
                    <h3><div className="h-icon"><XCircle size={18}/></div> Fermetures exceptionnelles</h3>

                    <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                      {[{ k: 'day', label: 'Jour unique' }, { k: 'range', label: 'Periode / Vacances' }].map(function(item) {
                        const active = (newClosure.type || 'day') === item.k;
                        return (
                          <button key={item.k} type="button"
                            onClick={() => setNewClosure(c => ({ ...c, type: item.k, date_end: '' }))}
                            style={{ padding: '8px 16px', borderRadius: 10, border: '1.5px solid ' + (active ? '#0f172a' : '#e2e8f0'), background: active ? '#0f172a' : '#fff', color: active ? '#fff' : '#64748b', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
                            {item.label}
                          </button>
                        );
                      })}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: newClosure.type === 'range' ? '1fr 1fr 2fr auto' : '1fr 2fr auto', gap: 12, marginBottom: 24, alignItems: 'end' }}>
                      <div className="s-field" style={{ margin: 0 }}>
                        <label>{newClosure.type === 'range' ? 'Du' : 'Date'}</label>
                        <input type="date" value={newClosure.date} onChange={e => setNewClosure(c => ({ ...c, date: e.target.value }))} />
                      </div>
                      {newClosure.type === 'range' && (
                        <div className="s-field" style={{ margin: 0 }}>
                          <label>Au (inclus)</label>
                          <input type="date" value={newClosure.date_end || ''} min={newClosure.date} onChange={e => setNewClosure(c => ({ ...c, date_end: e.target.value }))} />
                        </div>
                      )}
                      <div className="s-field" style={{ margin: 0 }}>
                        <label>Motif</label>
                        <input value={newClosure.label} onChange={e => setNewClosure(c => ({ ...c, label: e.target.value }))} placeholder={newClosure.type === 'range' ? 'ex: Conges ete, Vacances Noel...' : 'ex: Fete nationale, Pont...'} />
                      </div>
                      <button type="button" onClick={() => {
                        const type = newClosure.type || 'day';
                        if (!newClosure.date || !newClosure.label) return;
                        if (type === 'range' && !newClosure.date_end) return;
                        const entry = { date: newClosure.date, label: newClosure.label, type };
                        if (type === 'range') entry.date_end = newClosure.date_end;
                        setClosures(cl => [...cl, entry].sort((a, b) => a.date.localeCompare(b.date)));
                        setNewClosure({ date: '', label: '', type: newClosure.type || 'day', date_end: '' });
                      }} style={{ height: 48, padding: '0 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 14, fontWeight: 800, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Plus size={16}/> Ajouter
                      </button>
                    </div>

                    {closures.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                        <p style={{ margin: 0, fontWeight: 600 }}>Aucune fermeture exceptionnelle enregistree</p>
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {closures.map((c, i) => {
                        const isRange = c.type === 'range' && c.date_end;
                        const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: isRange ? '#fdf4ff' : '#fff7ed', border: '1.5px solid ' + (isRange ? '#e9d5ff' : '#fed7aa'), borderRadius: 14 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{c.label}</span>
                                {isRange && <span style={{ fontSize: 10, fontWeight: 800, background: '#a855f7', color: '#fff', padding: '2px 8px', borderRadius: 20 }}>PERIODE</span>}
                              </div>
                              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                                {isRange ? fmtDate(c.date) + ' -> ' + fmtDate(c.date_end) : fmtDate(c.date)}
                              </div>
                            </div>
                            <button type="button" onClick={() => setClosures(cl => cl.filter((_, j) => j !== i))} style={{ width: 32, height: 32, border: 'none', background: '#fff1f2', borderRadius: 8, color: '#f43f5e', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Trash2 size={14}/>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
            </form>

        </main>
      </div>

      <style>{`
        .settings-page { display: flex; flex-direction: column; gap: 30px; animation: fadeIn 0.4s ease; padding-bottom: 50px; }
        
        .settings-header { display: flex; justify-content: space-between; align-items: flex-end; }
        .settings-header h1 { font-size: 28px; font-weight: 900; margin: 0; color: #0f172a; }
        
        .btn-save-settings { 
          background: #0f172a; color: #fff; border: none; font-weight: 800; border-radius: 16px; 
          padding: 14px 24px; display: flex; align-items: center; gap: 12px; cursor: pointer; 
          box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.2); transition: all 0.2s; 
        }
        .btn-save-settings:hover { transform: translateY(-2px); box-shadow: 0 20px 25px -5px rgba(15, 23, 42, 0.25); }
        .btn-save-settings.loading { opacity: 0.7; }
        .spin { animation: spin 1s linear infinite; }

        .settings-container { display: grid; grid-template-columns: 280px 1fr; gap: 40px; align-items: start; }
        
        .settings-nav { background: #fff; padding: 10px; border-radius: 24px; border: 1px solid var(--admin-border); display: flex; flex-direction: column; gap: 5px; position: sticky; top: 20px; }
        .settings-nav button { 
          background: transparent; border: none; padding: 15px 20px; border-radius: 15px; 
          display: flex; align-items: center; gap: 15px; font-weight: 800; color: #64748b; 
          cursor: pointer; transition: all 0.2s; text-align: left; 
        }
        .settings-nav button.active { background: #f8fafc; color: var(--admin-primary); }
        .settings-nav button:hover:not(.active) { background: #f1f5f9; color: #0f172a; }

        .settings-card { background: #fff; border-radius: 32px; padding: 35px; border: 1px solid var(--admin-border); box-shadow: 0 4px 6px rgba(0,0,0,0.02); }
        .settings-card h3 { margin: 0 0 30px 0; font-size: 18px; font-weight: 900; display: flex; align-items: center; gap: 15px; color: #0f172a; }
        .h-icon { width: 36px; height: 36px; border-radius: 10px; background: #f1f5f9; color: #64748b; display: flex; align-items: center; justify-content: center; }

        .s-field { display: flex; flex-direction: column; gap: 10px; margin-bottom: 25px; }
        .s-field label { font-size: 11px; font-weight: 900; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 8px; }
        .s-field input, .s-field textarea { background: #f8fafc; border: 1.5px solid #e2e8f0; padding: 14px 18px; border-radius: 16px; font-weight: 700; color: #0f172a; outline: none; transition: all 0.2s; }
        .s-field input:focus, .s-field textarea:focus { border-color: var(--admin-primary); background: #fff; box-shadow: 0 0 0 4px rgba(234, 179, 8, 0.1); }
        
        .field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }

        /* LOGO STYLE */
        .logo-box { display: flex; align-items: center; gap: 25px; background: #f8fafc; padding: 25px; border-radius: 24px; border: 1px solid #eef2f6; }
        .logo-preview { width: 100px; height: 100px; border-radius: 20px; overflow: hidden; background: #fff; border: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 15px rgba(0,0,0,0.05); }
        .logo-preview img { width: 80%; height: 80%; object-fit: contain; }
        .no-logo { color: #cbd5e1; }
        .logo-actions { display: flex; flex-direction: column; gap: 8px; flex: 1; }
        .btn-upload { background: #fff; border: 1.5px solid #e2e8f0; padding: 8px 15px; border-radius: 12px; font-weight: 800; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: all 0.2s; }
        .btn-upload:hover { border-color: var(--admin-primary); color: var(--admin-primary); }
        .btn-del-mini { width: 32px; height: 32px; border-radius: 10px; border: none; background: #fff1f2; color: #f43f5e; cursor: pointer; }
        .hint { margin: 5px 0 0 0; font-size: 11px; color: #94a3b8; font-weight: 700; }

        /* SOCIAL FIELDS */
        .s-field.social { flex-direction: row; align-items: center; background: #f8fafc; padding: 5px 15px; border-radius: 18px; border: 1.5px solid #eef2f6; }
        .s-field.social input { border: none !important; background: transparent !important; box-shadow: none !important; padding: 10px 0 !important; }
        .s-entry { flex: 1; display: flex; flex-direction: column; gap: 2px; }
        .icon-fb { color: #1877f2; margin-right: 15px; }
        .icon-ig { color: #e4405f; margin-right: 15px; }
        .icon-in { color: #0077b5; margin-right: 15px; }

        .info-box-s { background: #f0f9ff; color: #0369a1; padding: 15px; border-radius: 16px; font-size: 12px; font-weight: 700; line-height: 1.4; display: flex; align-items: center; gap: 12px; border: 1px solid #e0f2fe; }
        .info-box-s.danger { background: #fff1f2; color: #be123c; border-color: #ffe4e6; }
        
        .alert-success-s { position: fixed; bottom: 30px; right: 30px; background: #10b981; color: #fff; padding: 15px 25px; border-radius: 16px; display: flex; align-items: center; gap: 12px; font-weight: 800; box-shadow: 0 20px 25px -5px rgba(16, 185, 129, 0.3); z-index: 100; animation: slideInRight 0.3s ease; }
        .alert-error-s { position: fixed; bottom: 30px; right: 30px; background: #f43f5e; color: #fff; padding: 15px 25px; border-radius: 16px; display: flex; align-items: center; gap: 12px; font-weight: 800; box-shadow: 0 20px 25px -5px rgba(244, 63, 94, 0.3); z-index: 100; animation: slideInRight 0.3s ease; }

        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animation-slide-up { animation: slideUp 0.4s ease; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

        @media (max-width: 900px) {
          .settings-container { grid-template-columns: 1fr; gap: 20px; }
          .settings-nav { flex-direction: row; overflow-x: auto; white-space: nowrap; padding: 8px; position: static; }
          .settings-nav button span { display: none; }
          .field-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
